import React from 'react'
import config from './config'
import TransitionGroupContext from './TransitionGroupContext'
import { forceReflow } from './utils/reflow'
import {
  UNMOUNTED,
  EXITED,
  ENTERING,
  ENTERED,
  EXITING,
  TransitionProps,
  TransitionStatus,
} from './types'

const noop = () => {}

interface TransitionState {
  status: TransitionStatus
}

/**
 * The Transition component lets you describe a transition from one component
 * state to another over time with a simple declarative API.
 *
 * This is a TypeScript port of react-transition-group's Transition component,
 * optimized for React 19 compatibility.
 */
class Transition<
  RefElement extends HTMLElement | undefined = undefined,
> extends React.Component<TransitionProps<RefElement>, TransitionState> {
  static contextType = TransitionGroupContext
  declare context: React.ContextType<typeof TransitionGroupContext>

  static defaultProps = {
    in: false,
    mountOnEnter: false,
    unmountOnExit: false,
    appear: false,
    enter: true,
    exit: true,
    onEnter: noop,
    onEntering: noop,
    onEntered: noop,
    onExit: noop,
    onExiting: noop,
    onExited: noop,
  }

  private appearStatus: TransitionStatus | null = null
  private nextCallback:
    | (((event?: any) => void) & { cancel?: () => void })
    | null = null

  constructor(props: TransitionProps<RefElement>, context: any) {
    super(props, context)

    const parentGroup = context
    // In the context of a TransitionGroup all enters are really appears
    const appear =
      parentGroup && !parentGroup.isMounting ? props.enter : props.appear

    let initialStatus: TransitionStatus

    if (props.in) {
      if (appear) {
        initialStatus = EXITED
        this.appearStatus = ENTERING
      } else {
        initialStatus = ENTERED
      }
    } else {
      if (props.unmountOnExit || props.mountOnEnter) {
        initialStatus = UNMOUNTED
      } else {
        initialStatus = EXITED
      }
    }

    this.state = { status: initialStatus }
  }

  static getDerivedStateFromProps(
    { in: nextIn }: TransitionProps<any>,
    prevState: TransitionState
  ): Partial<TransitionState> | null {
    if (nextIn && prevState.status === UNMOUNTED) {
      return { status: EXITED }
    }
    return null
  }

  componentDidMount() {
    this.updateStatus(true, this.appearStatus)
  }

  componentDidUpdate(prevProps: TransitionProps<RefElement>) {
    let nextStatus: TransitionStatus | null = null
    if (prevProps !== this.props) {
      const { status } = this.state

      if (this.props.in) {
        if (status !== ENTERING && status !== ENTERED) {
          nextStatus = ENTERING
        }
      } else {
        if (status === ENTERING || status === ENTERED) {
          nextStatus = EXITING
        }
      }
    }
    this.updateStatus(false, nextStatus)
  }

  componentWillUnmount() {
    this.cancelNextCallback()
  }

  getTimeouts() {
    const { timeout } = this.props
    let exit: number | undefined,
      enter: number | undefined,
      appear: number | undefined

    exit = enter = appear = timeout as number

    if (timeout != null && typeof timeout !== 'number') {
      exit = timeout.exit
      enter = timeout.enter
      appear = timeout.appear !== undefined ? timeout.appear : enter
    }
    return { exit, enter, appear }
  }

  updateStatus(mounting = false, nextStatus: TransitionStatus | null) {
    if (nextStatus !== null) {
      // nextStatus will always be ENTERING or EXITING.
      this.cancelNextCallback()

      if (nextStatus === ENTERING) {
        if (this.props.unmountOnExit || this.props.mountOnEnter) {
          const node = this.props.nodeRef
            ? (this.props.nodeRef as React.RefObject<RefElement>).current
            : undefined
          // With unmountOnExit or mountOnEnter, the enter animation should happen
          // at the transition between `exited` and `entering`.
          if (node) forceReflow(node as HTMLElement)
        }
        this.performEnter(mounting)
      } else {
        this.performExit()
      }
    } else if (this.props.unmountOnExit && this.state.status === EXITED) {
      this.setState({ status: UNMOUNTED })
    }
  }

  performEnter(mounting: boolean) {
    const { enter } = this.props
    const appearing = this.context ? this.context.isMounting : mounting
    const [maybeNode, maybeAppearing] = this.props.nodeRef
      ? [appearing]
      : [undefined, appearing]

    const timeouts = this.getTimeouts()
    const enterTimeout = appearing ? timeouts.appear : timeouts.enter

    // no enter animation skip right to ENTERED
    if ((!mounting && !enter) || config.disabled) {
      this.safeSetState({ status: ENTERED }, () => {
        this.props.onEntered?.(maybeNode as any, maybeAppearing as any)
      })
      return
    }

    this.props.onEnter?.(maybeNode as any, maybeAppearing as any)

    this.safeSetState({ status: ENTERING }, () => {
      this.props.onEntering?.(maybeNode as any, maybeAppearing as any)

      this.onTransitionEnd(enterTimeout, () => {
        this.safeSetState({ status: ENTERED }, () => {
          this.props.onEntered?.(maybeNode as any, maybeAppearing as any)
        })
      })
    })
  }

  performExit() {
    const { exit } = this.props
    const timeouts = this.getTimeouts()
    const maybeNode = this.props.nodeRef ? undefined : undefined

    // no exit animation skip right to EXITED
    if (!exit || config.disabled) {
      this.safeSetState({ status: EXITED }, () => {
        this.props.onExited?.(maybeNode as any)
      })
      return
    }

    this.props.onExit?.(maybeNode as any)

    this.safeSetState({ status: EXITING }, () => {
      this.props.onExiting?.(maybeNode as any)

      this.onTransitionEnd(timeouts.exit, () => {
        this.safeSetState({ status: EXITED }, () => {
          this.props.onExited?.(maybeNode as any)
        })
      })
    })
  }

  cancelNextCallback() {
    if (this.nextCallback !== null) {
      this.nextCallback.cancel?.()
      this.nextCallback = null
    }
  }

  safeSetState(nextState: TransitionState, callback?: () => void) {
    // This shouldn't be necessary, but there are weird race conditions with
    // setState callbacks and unmounting in testing, so always make sure that
    // we can cancel any pending setState callbacks after we unmount.
    const safeCallback = this.setNextCallback(callback)
    this.setState(nextState, safeCallback)
  }

  setNextCallback(callback?: () => void) {
    let active = true

    this.nextCallback = () => {
      if (active) {
        active = false
        this.nextCallback = null
        callback?.()
      }
    }

    this.nextCallback.cancel = () => {
      active = false
    }

    return this.nextCallback
  }

  onTransitionEnd(timeout: number | undefined, handler: () => void) {
    this.setNextCallback(handler)
    const node = this.props.nodeRef
      ? (this.props.nodeRef as React.RefObject<RefElement>).current
      : undefined

    const doesNotHaveTimeoutOrListener =
      timeout == null && !this.props.addEndListener
    if (!node || doesNotHaveTimeoutOrListener) {
      setTimeout(this.nextCallback!, 0)
      return
    }

    if (this.props.addEndListener) {
      const [maybeNode, maybeNextCallback] = this.props.nodeRef
        ? [this.nextCallback]
        : [node, this.nextCallback]
      this.props.addEndListener(maybeNode as any, maybeNextCallback as any)
    }

    if (timeout != null) {
      setTimeout(this.nextCallback!, timeout)
    }
  }

  render() {
    const status = this.state.status

    if (status === UNMOUNTED) {
      return null
    }

    const {
      children,
      // filter props for `Transition`
      in: _in,
      mountOnEnter: _mountOnEnter,
      unmountOnExit: _unmountOnExit,
      appear: _appear,
      enter: _enter,
      exit: _exit,
      timeout: _timeout,
      addEndListener: _addEndListener,
      onEnter: _onEnter,
      onEntering: _onEntering,
      onEntered: _onEntered,
      onExit: _onExit,
      onExiting: _onExiting,
      onExited: _onExited,
      nodeRef: _nodeRef,
      ...childProps
    } = this.props

    return (
      <TransitionGroupContext.Provider value={null}>
        {typeof children === 'function'
          ? children(status, childProps)
          : React.cloneElement(
              React.Children.only(children as React.ReactElement),
              childProps
            )}
      </TransitionGroupContext.Provider>
    )
  }
}

export default Transition
